import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import { CSVLink, CSVDownload } from 'react-csv';
import api from '../../services/api.js';
import { formatDateWithTimeYear } from '../../utils/dateFormat.js';
import FilterBar from './components/FilterBar';
import ImageModal from './components/ImageModal';
import MessageIcon from './components/MessageIcon';
import { useChatContext } from '../../context/ChatContext';
import { usePersistedFilterState } from './utils/usePersistedFilterState';

const STATUS_LABELS = {
    '-1': 'Pending Request', 1: 'Draft', 2: 'Saved', 3: 'Picked', 4: 'Completed',
    5: 'Rejected', 6: 'Accepted', 7: 'Payment Success', 8: 'Order Created', 9: 'Completed',
};
const STATUS_BADGE = {
    '-1': 'bg-amber-100 text-amber-700', 1: 'bg-gray-100 text-gray-600', 2: 'bg-gray-100 text-gray-600',
    3: 'bg-blue-100 text-blue-700', 4: 'bg-emerald-100 text-emerald-700', 5: 'bg-red-100 text-red-700',
    6: 'bg-emerald-100 text-emerald-700', 7: 'bg-emerald-100 text-emerald-700',
    8: 'bg-purple-100 text-purple-700', 9: 'bg-emerald-100 text-emerald-700',
};
const statusLabel = (s) => STATUS_LABELS[s] ?? 'Unknown';
const statusBadge = (s) => STATUS_BADGE[s] ?? 'bg-gray-100 text-gray-600';

const DETAIL_PAGE_SIZE = 15;
const INITIAL_LIST_STATE = { items: [], total: 0, hasMore: true, loading: true, loadingMore: false, error: false };

// Pre-migration designer_logs rows have no video_url — a video upload's URL sits in
// img_url instead. Sniff the extension so those old rows still render as video.
const isVideoUrl = (url) => /\.(mp4|webm|ogg|mov|avi)(\?.*)?$/i.test(url || '');

// Formats decimal hours as the 2 most significant units (e.g. "2mo 5d", "3d 4h", "5h 20m", "42m").
const formatDurationHours = (hours) => {
    const h = Number(hours);
    if (!Number.isFinite(h)) return '-';
    const totalMinutes = Math.round(h * 60);
    if (totalMinutes <= 0) return '0m';
    const MIN_PER_HOUR = 60, MIN_PER_DAY = 24 * 60, MIN_PER_MONTH = 30 * 24 * 60;
    let rem = totalMinutes;
    const months = Math.floor(rem / MIN_PER_MONTH); rem -= months * MIN_PER_MONTH;
    const days = Math.floor(rem / MIN_PER_DAY); rem -= days * MIN_PER_DAY;
    const hrs = Math.floor(rem / MIN_PER_HOUR); rem -= hrs * MIN_PER_HOUR;
    const mins = rem;
    const parts = [];
    if (months) parts.push(`${months}mo`);
    if (days) parts.push(`${days}d`);
    if (hrs) parts.push(`${hrs}h`);
    if (mins) parts.push(`${mins}m`);
    return parts.slice(0, 2).join(' ');
};

// Formats the date picker's "YYYY-MM-DD" string as a short date for the dynamic "Assigned before" column label.
const formatShortDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Drives both the summary table's columns (header + cell) and its bucket-filter clicks.
// Shown regardless of the By Status / By Attempts toggle.
const COMMON_COLUMNS = [
    { key: 'total_creatives_assigned', label: 'Total Assigned', bucket: 'all', info: 'Unique creatives currently assigned to this designer in the selected date range, regardless of status.' },
];

// "By Status" — the assignment/approval funnel, status-driven counts.
const STATUS_COLUMNS = [
    { key: 'unpicked_count', label: 'Unpicked', bucket: 'unpicked', info: 'Assigned but not yet picked up by the designer.' },
    { key: 'picked_count', label: 'Picked', bucket: 'picked', info: 'Picked up by the designer and in progress.' },
    { key: 'uploaded_count', label: 'Uploaded', bucket: 'uploaded', info: 'A design has been uploaded at least once.' },
    { key: 'waiting_approval_count', label: 'Waiting for Approval', bucket: 'waiting_approval', info: 'Uploaded and pending approval.' },
    { key: 'approved_count', label: 'Approved', bucket: 'approved', info: 'Approved design, ready to go live.' },
    { key: 'published_count', label: 'Published', bucket: 'published', info: 'Order created; the creative has gone live.' },
];

// "By Attempts" — time-to-upload and retry-count breakdown.
const ATTEMPT_COLUMNS = [
    { key: 'avg_time_taken_hours', label: 'Avg Time', format: formatDurationHours, info: 'Average time from assignment to first upload.' },
    { key: 'designs_lt_1hr', label: '<1 Hr', bucket: 'lt_1hr', info: 'First upload took under 1 hour from assignment.', separatorBefore: true },
    { key: 'designs_1_2hrs', label: '1-2 Hrs', bucket: '1_2hrs', info: 'First upload took 1-2 hours from assignment.' },
    { key: 'designs_2_3hrs', label: '2-3 Hrs', bucket: '2_3hrs', info: 'First upload took 2-3 hours from assignment.' },
    { key: 'designs_gt_3hrs', label: '3+ Hrs', bucket: 'gt_3hrs', info: 'First upload took over 3 hours from assignment.' },
    { key: 'iteration_1', label: '1st Try', bucket: 'iteration_1', info: 'Uploaded successfully on the 1st attempt.', separatorBefore: true },
    { key: 'iteration_2', label: '2nd Try', bucket: 'iteration_2', info: 'Took 2 upload attempts.' },
    { key: 'iteration_3', label: '3rd Try', bucket: 'iteration_3', info: 'Took 3 upload attempts.' },
    { key: 'iteration_4', label: '4th Try', bucket: 'iteration_4', info: 'Took 4 upload attempts.' },
    { key: 'iteration_5_plus', label: '5+ Tries', bucket: 'iteration_5_plus', info: 'Took 5 or more upload attempts.' },
    { key: 'unfinished_count', label: 'Unfinished', bucket: 'unfinished', info: 'Assigned, but no upload has been logged yet.' },
];

const creativesToCsv = (items) => items.map((c) => ({
    'Creative Name': c.creative_name || '-',
    'Sub Type': c.ad_creative_sub_type || '-',
    'Sales Person': c.sales_person_name || '-',
    'Status': statusLabel(c.status),
    'Assigned At': c.assigned_at ? formatDateWithTimeYear(c.assigned_at) : '-',
    'Last Upload Image': c.last_upload_img || '',
    'Last Upload Video': c.last_upload_video || '',
    'Uploaded At': c.last_upload_time ? formatDateWithTimeYear(c.last_upload_time) : '-',
    'Upload Count': c.total_tries ?? 0,
    'Published Date': c.order_created_at ? formatDateWithTimeYear(c.order_created_at) : '-',
    'Order ID': c.order_id || '-',
    'Order Name': c.order_name || '-',
    'Campaign ID': c.campaign_id || '-',
    'Campaign Name': c.campaign_name || '-',
}));

// Toggles a column's sort: unsorted -> asc -> desc -> unsorted (back to backend's default order).
const nextSort = (prev, key) => {
    if (prev.key !== key) return { key, direction: 'asc' };
    if (prev.direction === 'asc') return { key, direction: 'desc' };
    return { key: null, direction: 'asc' };
};

const sortRows = (rows, sort) => {
    if (!sort.key) return rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        // Postgres bigint aggregates (COUNT/SUM) arrive as numeric strings via node-postgres,
        // so typeof alone can't distinguish them from real text columns like designer_name.
        const an = Number(av), bn = Number(bv);
        const bothNumeric = av !== null && av !== '' && bv !== null && bv !== '' && !Number.isNaN(an) && !Number.isNaN(bn);
        if (bothNumeric) return dir * (an - bn);
        return dir * String(av ?? '').localeCompare(String(bv ?? ''));
    });
};

const SortableTh = ({ label, sortKey, align = 'right', sort, onSort, info, separatorBefore }) => {
    const active = sort.key === sortKey;
    const iconName = active ? (sort.direction === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevrons-up-down';
    return (
        <th
            className={`px-2 py-2 whitespace-nowrap cursor-pointer select-none hover:text-gray-800 ${align === 'left' ? 'text-left' : 'text-right'} ${separatorBefore ? 'border-l-2 border-gray-300' : ''}`}
            onClick={() => onSort(sortKey)}
        >
            <span className={`inline-flex items-center gap-1 w-full ${align === 'left' ? 'justify-start' : 'justify-end'}`}>
                <span title={info}>{label}</span>
                <SlIcon library="lucide" name={iconName} className={`text-base shrink-0 ${active ? 'text-sky-600' : 'text-gray-500'}`} />
            </span>
        </th>
    );
};

const CreativeRow = ({ c, index, onPreview }) => {
    const { openChat } = useChatContext();
    return (
        <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => openChat(c)}>
            <td className="px-3 py-2 text-gray-400 text-xs whitespace-nowrap">{index + 1}</td>
            <td className="px-3 py-2 whitespace-nowrap max-w-[220px]">
                <div className="font-medium text-gray-800 truncate">{c.creative_name || '-'}</div>
                {c.ad_creative_sub_type && <div className="text-xs text-gray-400">{c.ad_creative_sub_type}</div>}
            </td>
            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{c.sales_person_name || '-'}</td>
            <td className="px-3 py-2">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusBadge(c.status)}`}>
                    {statusLabel(c.status)}
                </span>
            </td>
            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                {c.assigned_at ? formatDateWithTimeYear(c.assigned_at) : '-'}
            </td>
            <td className="px-3 py-2">
                {c.last_upload_video ? (
                    <video
                        src={c.last_upload_video}
                        poster={c.last_upload_img || undefined}
                        onClick={(e) => { e.stopPropagation(); onPreview(c.last_upload_video); }}
                        className="w-12 h-12 object-cover rounded-md border border-gray-200 bg-gray-50 cursor-pointer hover:opacity-80 transition-opacity"
                        muted
                    />
                ) : c.last_upload_img && isVideoUrl(c.last_upload_img) ? (
                    <video
                        src={c.last_upload_img}
                        onClick={(e) => { e.stopPropagation(); onPreview(c.last_upload_img); }}
                        className="w-12 h-12 object-cover rounded-md border border-gray-200 bg-gray-50 cursor-pointer hover:opacity-80 transition-opacity"
                        muted
                    />
                ) : c.last_upload_img ? (
                    <img
                        src={c.last_upload_img}
                        alt=""
                        onClick={(e) => { e.stopPropagation(); onPreview(c.last_upload_img); }}
                        className="w-12 h-12 object-cover rounded-md border border-gray-200 bg-gray-50 cursor-pointer hover:opacity-80 transition-opacity"
                    />
                ) : (
                    <span className="text-red-400 italic whitespace-nowrap">Not uploaded yet</span>
                )}
            </td>
            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                {c.last_upload_time ? formatDateWithTimeYear(c.last_upload_time) : '-'}
            </td>
            <td className="px-3 py-2 text-gray-600 text-center whitespace-nowrap">
                {c.total_tries ?? 0}
            </td>
            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                {c.order_created_at ? formatDateWithTimeYear(c.order_created_at) : '-'}
            </td>
            <td className="px-3 py-2 whitespace-nowrap">
                <div className="text-gray-800">{c.order_name || '-'}</div>
                {c.order_id && <div className="text-xs text-gray-400">{c.order_id}</div>}
            </td>
            <td className="px-3 py-2 whitespace-nowrap">
                <div className="text-gray-800">{c.campaign_name || '-'}</div>
                {c.campaign_id && <div className="text-xs text-gray-400">{c.campaign_id}</div>}
            </td>
            <td className="px-3 py-2 text-center">
                <MessageIcon row={c} />
            </td>
        </tr>
    );
};

const CreativesModal = ({ designerId, designerName, bucket, bucketLabel, onClose, onFetchPage, onFetchAllCreatives }) => {
    const [state, setState] = useState(INITIAL_LIST_STATE);
    const stateRef = useRef(state);
    stateRef.current = state;
    const sentinelRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [exportRows, setExportRows] = useState(null);
    const [exportNonce, setExportNonce] = useState(0);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(false);

    const loadPage = useCallback(async (offset) => {
        const isFirst = offset === 0;
        setState((prev) => ({ ...prev, loading: isFirst, loadingMore: !isFirst, error: false }));
        try {
            const { items: newItems, total } = await onFetchPage(designerId, bucket, offset);
            setState((prev) => {
                const prevItems = isFirst ? [] : prev.items;
                const items = [...prevItems, ...newItems];
                return { items, total, hasMore: items.length < total, loading: false, loadingMore: false, error: false };
            });
        } catch (err) {
            console.error('Error fetching creatives:', err);
            setState((prev) => ({ ...prev, loading: false, loadingMore: false, error: true }));
        }
    }, [designerId, bucket, onFetchPage]);

    // Resets and reloads whenever the designer or bucket filter changes.
    useEffect(() => {
        setState(INITIAL_LIST_STATE);
        loadPage(0);
    }, [designerId, bucket, loadPage]);

    useEffect(() => {
        if (!state.hasMore || state.loading || state.error) return;
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0].isIntersecting) loadPage(stateRef.current.items.length); },
            { rootMargin: '150px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [state.hasMore, state.loading, state.error, loadPage]);

    useEffect(() => {
        const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const handleExport = async () => {
        setExporting(true);
        setExportError(false);
        try {
            const allItems = await onFetchAllCreatives(designerId);
            setExportRows(creativesToCsv(allItems));
            setExportNonce((n) => n + 1);
        } catch (err) {
            console.error('Error exporting creatives:', err);
            setExportError(true);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="w-full sm:w-[90vw] max-h-[85vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-gray-800 truncate">{designerName}</h3>
                        <p className="text-xs text-gray-500">{bucketLabel || 'All Creatives'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={exporting}
                            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                            {exporting ? 'Exporting...' : 'Download CSV'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
                            aria-label="Close"
                        >
                            <SlIcon library="lucide" name="x" className="text-lg" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-auto">
                    {state.loading ? (
                        <div className="text-center text-gray-400 text-sm py-10">Loading creatives...</div>
                    ) : state.error && state.items.length === 0 ? (
                        <div className="text-center text-sm py-10">
                            <span className="text-red-500">Failed to load creatives.</span>{' '}
                            <button type="button" onClick={() => loadPage(0)} className="text-sky-600 underline">Retry</button>
                        </div>
                    ) : !state.items.length ? (
                        <div className="text-center text-gray-400 text-sm py-10">No creatives found for this filter.</div>
                    ) : (
                        <>
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide sticky top-0">
                                <tr>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">#</th>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">Creative</th>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">Sales Person</th>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">Status</th>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">Assigned At</th>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">Last Upload</th>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">Uploaded At</th>
                                    <th className="px-3 py-2 text-center whitespace-nowrap">Upload Count</th>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">Published Date</th>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">Order</th>
                                    <th className="px-3 py-2 text-left whitespace-nowrap">Campaign</th>
                                    <th className="px-3 py-2 text-center whitespace-nowrap">Chat</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {state.items.map((c, index) => (
                                    <CreativeRow key={c.creative_id} c={c} index={index} onPreview={setPreviewUrl} />
                                ))}
                            </tbody>
                        </table>
                        <div ref={sentinelRef} className="h-2" />
                        {state.loadingMore && <div className="text-center text-gray-400 text-xs py-2">Loading more...</div>}
                        {state.error && state.items.length > 0 && (
                            <div className="text-center text-xs py-2">
                                <span className="text-red-500">Failed to load more.</span>{' '}
                                <button type="button" onClick={() => loadPage(state.items.length)} className="text-sky-600 underline">Retry</button>
                            </div>
                        )}
                        {!state.hasMore && !state.error && (
                            <div className="text-center text-gray-300 text-xs py-2">Showing all {state.total} of {state.total}</div>
                        )}
                        </>
                    )}
                </div>
                {exportError && <div className="text-center text-xs text-red-500 py-1 shrink-0">Failed to export creatives.</div>}
            </div>
            {exportRows && (
                <CSVDownload key={exportNonce} data={exportRows} filename={`${designerName || designerId}_creatives.csv`} target="_blank" />
            )}
            <ImageModal
                isOpen={!!previewUrl}
                image={previewUrl}
                alt="Last upload preview"
                onClose={() => setPreviewUrl(null)}
            />
        </div>
    );
};

const CLICKABLE_CELL = 'px-2 py-2 text-right tabular-nums text-sky-700 underline decoration-dotted underline-offset-2 hover:text-sky-900 cursor-pointer';
const PLAIN_CELL = 'px-2 py-2 text-right tabular-nums text-gray-600';

const DesignerPerformanceReport = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeModal, setActiveModal] = useState(null);
    const [designers, setDesigners] = useState([]);
    const [dateRange, setDateRange] = usePersistedFilterState('alpha_designer.performance_report.dateRange', { startDate: null, endDate: null });
    const [designerFilter, setDesignerFilter] = usePersistedFilterState('alpha_designer.performance_report.designerFilter', null);
    const [sort, setSort] = useState({ key: null, direction: 'asc' });
    const [breakdownMode, setBreakdownMode] = useState('status');

    // Only shown once a start date is actually selected — the metric has no meaning without one.
    const assignBeforeStartColumn = useMemo(() => {
        if (!dateRange.startDate) return null;
        return {
            key: 'assign_before_start_count',
            bucket: 'assign_before_start',
            label: `Assigned before ${formatShortDate(dateRange.startDate)}`,
            info: 'Still assigned to this designer from before the selected start date.',
        };
    }, [dateRange.startDate]);
    const visibleColumns = useMemo(() => {
        if (breakdownMode === 'attempts') return [...COMMON_COLUMNS, ...ATTEMPT_COLUMNS];
        const statusCols = assignBeforeStartColumn ? [...STATUS_COLUMNS, assignBeforeStartColumn] : STATUS_COLUMNS;
        return [...COMMON_COLUMNS, ...statusCols];
    }, [breakdownMode, assignBeforeStartColumn]);

    const filtersRef = useRef({ dateRange, designerFilter });
    filtersRef.current = { dateRange, designerFilter };

    const designerOptions = useMemo(
        () => designers.map((d) => ({ value: d.designer_id, label: d.name })),
        [designers]
    );

    const fetchDesigners = useCallback(async () => {
        try {
            const res = await api({ url: '/alpha_cst_designer/getDesigners', method: 'POST', responseType: 'json', data: {} });
            if (res.code === 200) setDesigners(res.data?.data || []);
        } catch (err) {
            console.error('Error fetching designers:', err);
        }
    }, []);

    const fetchSummary = useCallback(async () => {
        setLoading(true);
        try {
            const { dateRange: dr, designerFilter: df } = filtersRef.current;
            const res = await api({
                url: '/alpha_cst_designer/designerPerformanceReport',
                method: 'POST',
                responseType: 'json',
                data: { startDate: dr.startDate, endDate: dr.endDate, designerId: df?.value },
            });
            if (res.code === 200) setRows(res.data?.data || []);
        } catch (err) {
            console.error('Error fetching designer performance report:', err);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDesigners(); }, [fetchDesigners]);

    useEffect(() => {
        setActiveModal(null);
        fetchSummary();
    }, [dateRange, designerFilter, fetchSummary]);

    const fetchDetailPage = useCallback(async (designerId, bucket, offset) => {
        const { dateRange: dr } = filtersRef.current;
        const res = await api({
            url: '/alpha_cst_designer/designerPerformanceReportDetail',
            method: 'POST',
            responseType: 'json',
            data: { designerId, offset, limit: DETAIL_PAGE_SIZE, startDate: dr.startDate, endDate: dr.endDate, bucket },
        });
        if (res.code !== 200) throw new Error('Request failed');
        return { items: res.data?.data || [], total: res.data?.total || 0 };
    }, []);

    // Unfiltered, full fetch — used only for the comprehensive CSV export.
    const fetchAllCreatives = useCallback(async (designerId) => {
        const { dateRange: dr } = filtersRef.current;
        const limit = rows.find((r) => r.designer_id === designerId)?.total_creatives_assigned || 1000;
        const res = await api({
            url: '/alpha_cst_designer/designerPerformanceReportDetail',
            method: 'POST',
            responseType: 'json',
            data: { designerId, offset: 0, limit, startDate: dr.startDate, endDate: dr.endDate },
        });
        if (res.code !== 200) throw new Error('Request failed');
        return res.data?.data || [];
    }, [rows]);

    const openModal = (designerId, designerName, bucket, label) => setActiveModal({ designerId, designerName, bucket, label });
    const handleSort = (key) => setSort((prev) => nextSort(prev, key));
    const sortedRows = useMemo(() => sortRows(rows, sort), [rows, sort]);

    // Sums every visible count column across all designers; Avg Time has no sensible "total" so it's left blank.
    const grandTotals = useMemo(() => {
        const totals = {};
        visibleColumns.forEach((c) => {
            if (c.key === 'avg_time_taken_hours') return;
            totals[c.key] = rows.reduce((sum, row) => sum + (Number(row[c.key]) || 0), 0);
        });
        return totals;
    }, [rows, visibleColumns]);

    // Always exports the full breakdown (both status and attempt columns), independent of the toggle.
    const overviewCsvData = useMemo(() => sortedRows.map((row) => ({
        'Designer ID': row.designer_id,
        'Designer Name': row.designer_name,
        'Total Assigned': row.total_creatives_assigned,
        'Unpicked': row.unpicked_count,
        'Picked': row.picked_count,
        'Uploaded': row.uploaded_count,
        'Waiting for Approval': row.waiting_approval_count,
        'Approved': row.approved_count,
        'Published': row.published_count,
        ...(assignBeforeStartColumn ? { [assignBeforeStartColumn.label]: row.assign_before_start_count } : {}),
        'Avg Time': formatDurationHours(row.avg_time_taken_hours),
        '<1 Hr': row.designs_lt_1hr,
        '1-2 Hrs': row.designs_1_2hrs,
        '2-3 Hrs': row.designs_2_3hrs,
        '3+ Hrs': row.designs_gt_3hrs,
        '1st Try': row.iteration_1,
        '2nd Try': row.iteration_2,
        '3rd Try': row.iteration_3,
        '4th Try': row.iteration_4,
        '5+ Tries': row.iteration_5_plus,
        'Unfinished': row.unfinished_count,
    })), [sortedRows, assignBeforeStartColumn]);

    return (
        <div className="p-4 min-h-[560px] w-full">
            <div className="flex items-end justify-between gap-3 flex-wrap mb-2">
                <div className="flex-1 min-w-0">
                    <FilterBar
                        dateRange={dateRange}
                        onDateChange={setDateRange}
                        designer={designerFilter}
                        onDesignerChange={setDesignerFilter}
                        designerOptions={designerOptions}
                        showSearch={false}
                        showAdType={false}
                        showStatus={false}
                    />
                </div>
                <div className="flex items-center gap-2 shrink-0 mb-4 flex-wrap">
                    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs shrink-0">
                        <button
                            type="button"
                            onClick={() => setBreakdownMode('status')}
                            className={`px-3 py-1 whitespace-nowrap ${breakdownMode === 'status' ? 'bg-sky-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >
                            By Status
                        </button>
                        <button
                            type="button"
                            onClick={() => setBreakdownMode('attempts')}
                            className={`px-3 py-1 whitespace-nowrap border-l border-gray-300 ${breakdownMode === 'attempts' ? 'bg-sky-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >
                            By Attempts
                        </button>
                    </div>
                    <div className="px-3 py-1 shadow rounded-lg bg-gray-200 text-sm whitespace-nowrap">
                        Designers: <span className="font-semibold">{rows.length}</span>
                    </div>
                    {rows.length > 0 && (
                        <CSVLink
                            data={overviewCsvData}
                            filename="designer_performance_overview.csv"
                            className="px-3 py-1 rounded-lg bg-gray-200 text-sm hover:bg-gray-300 whitespace-nowrap"
                        >
                            Download CSV
                        </CSVLink>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16 text-gray-400">Loading...</div>
            ) : rows.length === 0 ? (
                <div className="flex justify-center py-16 text-gray-400">No data found.</div>
            ) : (
                <div className="w-full overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                    <table className="w-full text-sm table-auto">
                        <thead className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
                            <tr>
                                <th className="w-8 px-2 py-2"></th>
                                <SortableTh label="Designer" sortKey="designer_name" align="left" sort={sort} onSort={handleSort} />
                                {visibleColumns.map((c) => (
                                    <SortableTh key={c.key} label={c.label} sortKey={c.key} sort={sort} onSort={handleSort} info={c.info} separatorBefore={c.separatorBefore} />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sortedRows.map((row) => (
                                <tr
                                    key={row.designer_id}
                                    className="hover:bg-sky-50 transition-colors cursor-pointer"
                                    onClick={() => openModal(row.designer_id, row.designer_name, 'all', 'All Creatives')}
                                >
                                    <td className="px-2 py-2 text-gray-400">
                                        <SlIcon library="lucide" name="chevron-right" className="text-base" />
                                    </td>
                                    <td className="px-2 py-2 font-medium text-gray-800 whitespace-nowrap">
                                        {row.designer_name}
                                        <div className="text-xs text-gray-400 font-normal">ID: {row.designer_id}</div>
                                    </td>
                                    {visibleColumns.map((c) => (
                                        <td
                                            key={c.key}
                                            className={`${c.bucket ? CLICKABLE_CELL : PLAIN_CELL} ${c.separatorBefore ? 'border-l-2 border-gray-300' : ''}`}
                                            onClick={c.bucket ? (e) => { e.stopPropagation(); openModal(row.designer_id, row.designer_name, c.bucket, c.label); } : undefined}
                                        >
                                            {c.format ? c.format(row[c.key]) : row[c.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                            <tr>
                                <td className="px-2 py-2"></td>
                                <td className="px-2 py-2 text-gray-800 whitespace-nowrap">Grand Total</td>
                                {visibleColumns.map((c) => (
                                    <td
                                        key={c.key}
                                        className={`px-2 py-2 text-right tabular-nums text-gray-800 ${c.separatorBefore ? 'border-l-2 border-gray-300' : ''}`}
                                    >
                                        {c.key === 'avg_time_taken_hours' ? '-' : grandTotals[c.key]}
                                    </td>
                                ))}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {activeModal && (
                <CreativesModal
                    designerId={activeModal.designerId}
                    designerName={activeModal.designerName}
                    bucket={activeModal.bucket}
                    bucketLabel={activeModal.label}
                    onClose={() => setActiveModal(null)}
                    onFetchPage={fetchDetailPage}
                    onFetchAllCreatives={fetchAllCreatives}
                />
            )}
        </div>
    );
};

export default DesignerPerformanceReport;
